import { map,
    ascend,
    addIndex,
    contains,
    filter,
    concat,
    compose,
    trim,
    findIndex,
    indexOf,
    merge,
    slice,
    sortBy,
    sortWith,
    prop,
    reverse,
    toLower
} from 'ramda';
import { createSelector } from 'reselect';
import moment from 'moment';

import { API_CALL_SUCCESS, HORSE_SET_EDIT_MODE } from '../constants/actionTypes';
import {
    HORSES_LIST,
} from '../constants/apiConstants';
import horseValues from './horseValues';
const initialState = [];

const apiHorsesReducer = (state, action) => {
    const {
        endpoint,
    } = action;

    switch (endpoint) {
    case HORSES_LIST:
        let favorites = localStorage.getItem('horses_K21H') ? JSON.parse(localStorage.getItem('horses_K21H')) : [];
        const horses =  map((horse) => {
            return compose(
                map((i) => {
                    return isNaN(parseFloat(i)) || Date.parse(i) || isNaN(Number(i)) ? i : parseFloat(i);
                }),
                map((i) => {
                    return typeof i === 'string' ? trim(i) : i;
                }),
            )({
                ...horse,
                video: horse.enhanced_featured_video,
                is_editing: false,
                favorited: favorites.includes(horse.hip)
            });
        }, action.response);
        return horses;
    default:
        return state;
    }
};

const horsesReducer = (state = initialState, action) => {
    const {
        type,
    } = action;

    switch (type) {
    case API_CALL_SUCCESS:
        return apiHorsesReducer(state, action);
    case HORSE_SET_EDIT_MODE:
        const horseIndex = findIndex(horse => horse.id === action.horse.id)(state);
        return [
            ...map(a => merge(a, { is_editing: false }))(slice(0, horseIndex, state)),
            merge(state[horseIndex], {
                is_editing: action.is_editing,
            }),
            ...map(a => merge(a, { is_editing: false }))(slice(horseIndex + 1, state.length, state)),
        ];
    case 'HORSE_UPDATE':
        const updateIndex = findIndex(horse => horse.id === action.horse.id)(state);
        return [
            ...slice(0, updateIndex, state),
            action.horse,
            ...slice(updateIndex + 1, state.length, state)
        ];
    default:
        return state;
    }
};

export const selectAllHorses = createSelector(
    state => state.horses,
    state => state.search,
    state => state.sale,
    state => state.sort,
    state => state.form,
    state => state.suggestionSearchActive,
    (_, props) => props && props.column,
    (_, props) => props && props.filter,
    (horses, search, sale, sort, form, suggestionSearchActive, columnParam = null, filterParam = null ) => {
        const filterHIP = filter((horse) => {
            return sale.max_hip >= horse.hip && sale.min_hip <= horse.hip;
        });

        const filterDay = filter((horse) => {
            return horse.session === form.saleSession.values.session;
        });

        let results = horses;
        const searchIndex = document.getElementById('searchTerm') ? document.getElementById('searchTerm').value : '';

        if (searchIndex && search[searchIndex].text) {
            const searchNumber = parseInt(search[searchIndex].text, 10);
            const searchIsNumber = !isNaN(searchNumber);
            const searchHorses = filter((horse) => {
                return contains(horse.id, search[searchIndex].result);
            }, horses);

            const searchHorseNumbers = searchIsNumber ? filter((horse) => {
                return horse.hip === searchNumber;
            }, horses) : [];

            results = concat(searchHorses, searchHorseNumbers);
            if (searchIndex !== 'horses' && suggestionSearchActive) {
                results = filter(horse => toLower(horse[searchIndex]) === toLower(search[searchIndex].text))(results);
            }
        }

        if (columnParam && filterParam && columnParam !== 'horses') {
            results = filter((horse) => {
                return horse[columnParam].indexOf(filterParam.toUpperCase()) !== -1;
            })(results);
        }

        const sortColumn = sortWith([
            ascend(prop(sort.column)),
            ascend(prop('hip'))
          ]);

        const outsOnly = filter((horse) => {
            return horse.out || horse.purchaser === 'OUT';
        }, results);
        const notBought = filter((horse) => {
            return horse.purchaser === 'NOT SOLD' && !horse.out;
        }, results);
        const notBoughtSorted = sort.direction === 'desc' ? sortColumn(notBought) : reverse(sortColumn(notBought));
        const noUTSTime = filter((horse) => {
            return !horse.under_tack_show_time && !horse.out && horse.purchaser !== 'OUT';
        }, results);
        const noStatus = filter((horse) => {
            return horse.purchaser === 'NO STATUS' && !horse.out && !horse.price;
        }, horses);

        const concattedChunks = concat(notBoughtSorted, concat(noStatus, outsOnly));

        if (sort.column === 'purchaser') {
            let purchaserSort = filter((horse) => {
                return horse.purchaser && horse.purchaser !== 'NOT SOLD' && horse.purchaser !== 'NO STATUS' && !horse.out && horse.purchaser !== 'OUT';
            }, results);
            purchaserSort = sort.direction === 'desc' ? reverse(sortColumn(purchaserSort)) : sortColumn(purchaserSort);
            results = concat(purchaserSort, concattedChunks);
        } else if (sort.column === 'covering_sire' || sort.column === 'name') {
            let horsesWithData = filter((horse) => {
                return horse[sort.column];
            }, results);
            let noDataInColumn = filter((horse) => {
                return !horse[sort.column];
            }, results);
            const horsesSorted = sort.direction === 'desc' ? reverse(sortColumn(horsesWithData)) : sortColumn(horsesWithData);
            results = concat(horsesSorted, noDataInColumn);
        } else if (sort.column === 'price') {
            let priceBoughtSort = filter((horse) => {
                return horse.price && !horse.out && horse.purchaser !== 'NOT SOLD' && horse.purchaser !== 'OUT';
            }, results);
            priceBoughtSort = sort.direction === 'desc' ? sortColumn(priceBoughtSort) : reverse(sortColumn(priceBoughtSort));
            results = concat(priceBoughtSort, concattedChunks);
        } else if (sort.column === 'under_tack_show_time') {
            let numericalTimeSort = filter((horse) => {
                return horse.under_tack_show_time && horse.under_tack_show_time !== 'G' && !horse.out && horse.purchaser !== 'OUT';
            }, results);
            let gallopSort = filter((horse) => {
                return horse.under_tack_show_time && horse.under_tack_show_time === 'G' && !horse.out && horse.purchaser !== 'OUT';
            }, results);
            numericalTimeSort = sort.direction === 'desc' ? reverse(sortColumn(numericalTimeSort)) : sortColumn(numericalTimeSort);
            gallopSort = sortColumn(gallopSort)
            let timeSort = concat(numericalTimeSort, gallopSort);
            results = concat(timeSort, concat(noUTSTime, outsOnly));
        } else if (sort.column === 'under_tack_show_video') {
            let horsesWithUTSVideos = filter((horse) => {
                return horse.under_tack_show_video;
            }, results);
            let horsesWithNoUTSVideos = filter((horse) => {
                return !horse.under_tack_show_video;
            }, results);

            horsesWithUTSVideos = sort.direction === 'desc' ? reverse(sortBy(prop('hip'), horsesWithUTSVideos)) : sortBy(prop('hip'), horsesWithUTSVideos);
            results = concat(horsesWithUTSVideos, horsesWithNoUTSVideos);
        } else if (sort.column === 'out_date') {
            let outHorses = map((horse) => {
                const outDate = horse.out_date && moment(horse.out_date, 'YYYY-MM-DDtHH:mm:ss.SSSSSSZZZZZ').isValid() ? moment(horse.out_date, 'YYYY-MM-DDtHH:mm:ss.SSSSSSZZZZZ') : null
                const displayDate = sale.earliest_out_date && moment(sale.earliest_out_date).isAfter(outDate) ? moment(sale.earliest_out_date) : outDate;
                return merge(
                    horse, {
                        out_date: displayDate,
                    }
                );
            })(results);

            results = sort.direction === 'desc' ? reverse(sortColumn(outHorses)) : sortColumn(outHorses);
        } else if (sort.column === 'pp_enabled') {
            let horsesWithPP = filter(horse => horse.pp_enabled)(results);
            const horsesNoPP = filter(horse => !horse.pp_enabled)(results);

            horsesWithPP = sort.direction === 'desc' ? reverse(sortColumn(horsesWithPP)) : sortColumn(horsesWithPP);

            results = concat(horsesWithPP, horsesNoPP);

        } else if (sort.column === 'generalhorsephoto_set') {
            let horsesWithMedia = filter(horse => horse.generalhorsephoto_set.length > 0 || horse.youtube_url)(results);
            const horsesNoMedia = filter(horse => horse.generalhorsephoto_set.length === 0 && !horse.youtube_url)(results);
            const mediaSort = sortWith([
              ascend(prop('hip'))
            ]);
            horsesWithMedia = sort.direction === 'desc' ? reverse(mediaSort(horsesWithMedia)) : mediaSort(horsesWithMedia);

            results = concat(horsesWithMedia, horsesNoMedia);

        } else if (sort.tab !== 'updates') {
            results = sort.direction === 'desc' ? reverse(sortColumn(results)) : sortColumn(results);
        }
        const {
            saleSession = {},
        } = form;

        if (saleSession.values) {
            results = saleSession.values && saleSession.values.session === 'ALL' ? results : filterDay(results);
        }

        return filterHIP(results);
    }
);

export const selectHorsePhotos = ({ generalhorsephoto_set }) => {
    return map(horsephoto => horsephoto.photo)(generalhorsephoto_set);
};

export default horsesReducer;
